import { ErrorState, PageLoader } from "@ark/ui";
import {
  Component,
  Suspense,
  lazy,
  type ErrorInfo,
  type ReactNode,
  useEffect,
  useMemo,
  useSyncExternalStore,
} from "react";
import {
  Navigate,
  Outlet,
  createBrowserRouter,
  useLocation,
  useNavigate,
  useParams,
} from "react-router";
import { RouterProvider } from "react-router/dom";

import { DesktopRuntimeBridge } from "./DesktopRuntimeBridge";
import { ApplicationWorkspaceCommit } from "./ApplicationWorkspaceCommit";
import { DesktopUpdater } from "./DesktopUpdater";
import { openPreferredApplicationPath } from "./applicationTargets";
import { MajorReleasePrompt } from "./MajorReleasePrompt";
import { desktopHomeRoute, isDesktopRuntime } from "./desktopRuntime";
import { setMajorReleasePromptActive } from "./productReleases";
import { isShareableDocsLocation } from "./publicDocsRoute";
import { sessionManager, useSession, useSessionReady } from "./session";
import { useApplications } from "./useApplications";

const HomeApp = lazy(() => import("home/App"));
const LoginPage = lazy(() =>
  import("./LoginPage").then(({ LoginPage: page }) => ({ default: page })),
);
const RegisterPage = lazy(() =>
  import("./RegisterPage").then(({ RegisterPage: page }) => ({
    default: page,
  })),
);
const ActivationPage = lazy(() =>
  import("./ActivationPage").then(({ ActivationPage: page }) => ({
    default: page,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import("./ForgotPasswordPage").then(({ ForgotPasswordPage: page }) => ({
    default: page,
  })),
);
const JoinTenantPage = lazy(() =>
  import("./JoinTenantPage").then(({ JoinTenantPage: page }) => ({
    default: page,
  })),
);
const TenantSettingsPage = lazy(() =>
  import("./TenantSettingsPage").then(({ TenantSettingsPage: page }) => ({
    default: page,
  })),
);
const ShellLayout = lazy(() =>
  import("./ShellLayout").then(({ ShellLayout: layout }) => ({
    default: layout,
  })),
);
const AccountPage = lazy(() =>
  import("./AccountPage").then(({ AccountPage: page }) => ({
    default: page,
  })),
);
const DirectoryPage = lazy(() =>
  import("./DirectoryPage").then(({ DirectoryPage: page }) => ({
    default: page,
  })),
);
const MembersPage = lazy(() =>
  import("./MembersPage").then(({ MembersPage: page }) => ({
    default: page,
  })),
);
const AppCenterPage = lazy(() =>
  import("./AppCenterPage").then(({ AppCenterPage: page }) => ({
    default: page,
  })),
);
const SkillPage = lazy(() =>
  import("./CapabilityPages").then((m) => ({ default: m.SkillPage })),
);
const ToolsPage = lazy(() =>
  import("./CapabilityPages").then((m) => ({ default: m.ToolsPage })),
);
const LegacyFaaSRedirect = lazy(() =>
  import("./CapabilityPages").then((m) => ({ default: m.LegacyFaaSRedirect })),
);
const ChangelogPage = lazy(() =>
  import("./ChangelogPage").then(({ ChangelogPage: page }) => ({
    default: page,
  })),
);
const DeveloperAccess = lazy(() =>
  import("./DeveloperAccess").then(({ DeveloperAccess: guard }) => ({
    default: guard,
  })),
);
const DeveloperAppsPage = lazy(() =>
  import("./DeveloperAppsPage").then(({ DeveloperAppsPage: page }) => ({
    default: page,
  })),
);
const DeveloperAppOverviewPage = lazy(() =>
  import("./DeveloperAppOverviewPage").then(
    ({ DeveloperAppOverviewPage: page }) => ({
      default: page,
    }),
  ),
);
const AgentConnectPage = lazy(() =>
  import("./AgentConnectPage").then(({ AgentConnectPage: page }) => ({
    default: page,
  })),
);
const TenantFaaSPage = lazy(() =>
  import("./TenantFaaSPage").then(({ TenantFaaSPage: page }) => ({
    default: page,
  })),
);
const ApprovalCenterPage = lazy(() =>
  import("./ApprovalCenterPage").then(({ ApprovalCenterPage: page }) => ({
    default: page,
  })),
);
const LaunchTargetHost = lazy(() =>
  import("./LaunchTargetHost").then(({ LaunchTargetHost: host }) => ({
    default: host,
  })),
);
const ExternalAppHost = lazy(() =>
  import("./ExternalAppHost").then(({ ExternalAppHost: host }) => ({
    default: host,
  })),
);
const OAuthAuthorizePage = lazy(() =>
  import("./OAuthAuthorizePage").then(({ OAuthAuthorizePage: page }) => ({
    default: page,
  })),
);
const StandaloneAppHost = lazy(() =>
  import("./StandaloneAppHost").then(({ StandaloneAppHost: host }) => ({
    default: host,
  })),
);

const router = createBrowserRouter([
  {
    element: (
      <Suspense fallback={<PageLoader label="正在打开会议邀请" />}>
        <MeetingJoinHost />
      </Suspense>
    ),
    path: "/join/meeting/:inviteCode",
  },
  {
    element: (
      <Suspense fallback={<PageLoader label="正在加载外部应用" />}>
        <ExternalAppHost />
      </Suspense>
    ),
    path: "/external/:app/*",
  },
  {
    element: <Authenticated />,
    children: [
      {
        element: (
          <Suspense fallback={<PageLoader label="正在加载租户管理" />}>
            <TenantSettingsPage />
          </Suspense>
        ),
        path: "/tenant/:section?",
      },
      {
        element: (
          <Suspense fallback={<PageLoader label="正在打开独立应用" />}>
            <StandaloneAppHost />
          </Suspense>
        ),
        path: "/open/:appSlug/*",
      },
      {
        element: (
          <Suspense fallback={<PageLoader label="正在加载 OAuth 授权页" />}>
            <OAuthAuthorizePage />
          </Suspense>
        ),
        path: "/oauth/authorize",
      },
      {
        element: (
          <Suspense fallback={<PageLoader label="正在加载控制台" />}>
            <ShellLayout />
          </Suspense>
        ),
        children: [
          { element: <RootApplication />, index: true },
          {
            element: <RemoteHome />,
            path: "home",
          },
          {
            element: (
              <Suspense fallback={<PageLoader label="正在打开应用" />}>
                <LaunchTargetHost />
              </Suspense>
            ),
            path: "apps/launch/:installationId/:targetId",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载账号中心" />}
              >
                <AccountPage />
              </WorkspaceSuspense>
            ),
            path: "me",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载应用中心" />}
              >
                <AppCenterPage />
              </WorkspaceSuspense>
            ),
            path: "apps",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载 Skill" />}
              >
                <SkillPage />
              </WorkspaceSuspense>
            ),
            path: "skills/*",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载工具市场" />}
              >
                <ToolsPage />
              </WorkspaceSuspense>
            ),
            path: "tools/*",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载更新日志" />}
              >
                <ChangelogPage />
              </WorkspaceSuspense>
            ),
            path: "changelog",
          },
          {
            element: (
              <Suspense fallback={<PageLoader label="正在验证开发者权限" />}>
                <DeveloperAccess />
              </Suspense>
            ),
            children: [
              {
                element: (
                  <WorkspaceSuspense
                    fallback={<PageLoader label="正在加载开发者应用" />}
                  >
                    <DeveloperAppsPage />
                  </WorkspaceSuspense>
                ),
                path: "developer/apps",
              },
              {
                element: (
                  <WorkspaceSuspense
                    fallback={<PageLoader label="正在加载应用概览" />}
                  >
                    <DeveloperAppOverviewPage />
                  </WorkspaceSuspense>
                ),
                path: "developer/apps/:appId",
              },
              {
                element: (
                  <WorkspaceSuspense
                    fallback={<PageLoader label="正在准备 Agent 接入" />}
                  >
                    <AgentConnectPage />
                  </WorkspaceSuspense>
                ),
                path: "developer/agent/connect",
              },
            ],
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载云函数" />}
              >
                <TenantFaaSPage />
              </WorkspaceSuspense>
            ),
            path: "developer/functions",
          },
          {
            element: (
              <Suspense fallback={<PageLoader />}>
                <LegacyFaaSRedirect />
              </Suspense>
            ),
            path: "developer/faas",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载审批中心" />}
              >
                <ApprovalCenterPage />
              </WorkspaceSuspense>
            ),
            path: "approvals",
          },
          {
            element: null,
            path: "apps/*",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载通讯录" />}
              >
                <DirectoryPage />
              </WorkspaceSuspense>
            ),
            path: "directory",
          },
          {
            element: (
              <WorkspaceSuspense
                fallback={<PageLoader label="正在加载企业通讯录" />}
              >
                <MembersPage />
              </WorkspaceSuspense>
            ),
            path: "members",
          },
          {
            element: (
              <WorkspaceSuspense fallback={<PageLoader label="正在加载" />}>
                <JoinTenantPage />
              </WorkspaceSuspense>
            ),
            path: "tenants/join",
          },
          {
            element: <LegacyTenantSettingsRedirect />,
            path: "tenants/settings",
          },
        ],
      },
    ],
  },
  {
    element: <Anonymous />,
    children: [
      {
        element: (
          <Suspense fallback={<PageLoader label="正在加载登录页" />}>
            <LoginPage />
          </Suspense>
        ),
        path: "/auth/login",
      },
      {
        element: (
          <Suspense fallback={<PageLoader label="正在加载注册页" />}>
            <RegisterPage />
          </Suspense>
        ),
        path: "/auth/register",
      },
      {
        element: (
          <Suspense fallback={<PageLoader label="正在加载激活页" />}>
            <ActivationPage />
          </Suspense>
        ),
        path: "/auth/activate",
      },
      {
        element: (
          <Suspense fallback={<PageLoader label="正在加载找回密码页" />}>
            <ForgotPasswordPage />
          </Suspense>
        ),
        path: "/auth/forgot-password",
      },
    ],
  },
  { element: <Navigate replace to="/" />, path: "*" },
]);

function subscribeToRouter(onStoreChange: () => void): () => void {
  return router.subscribe(() => onStoreChange());
}

export function App() {
  return (
    <>
      <DesktopRuntimeBridge navigate={(to) => void router.navigate(to)} />
      <DesktopUpdater />
      <RouterProvider router={router} />
      <MajorReleasePromptHost />
    </>
  );
}

function MajorReleasePromptHost() {
  const session = useSession();
  const sessionReady = useSessionReady();
  const pathname = useSyncExternalStore(
    subscribeToRouter,
    () => router.state.location.pathname,
    () => router.state.location.pathname,
  );
  const changelogOpen = pathname.startsWith("/changelog");
  const promptEligible = !changelogOpen && isWorkspacePath(pathname);

  useEffect(() => {
    if (sessionReady && (!session || !promptEligible)) {
      setMajorReleasePromptActive(false);
    }
  }, [promptEligible, session, sessionReady]);

  if (!sessionReady || !session || !promptEligible) return null;
  return (
    <MajorReleasePrompt
      key={`${session.tenant.id}:${session.user.id}`}
      onNavigate={(path) => void router.navigate(path)}
      tenantId={session.tenant.id}
      userId={session.user.id}
    />
  );
}

function isWorkspacePath(pathname: string): boolean {
  const standaloneRoots = [
    "/auth",
    "/external",
    "/join/meeting",
    "/oauth/authorize",
    "/open",
    "/tenant",
  ];
  return !standaloneRoots.some(
    (root) => pathname === root || pathname.startsWith(`${root}/`),
  );
}

function LegacyTenantSettingsRedirect() {
  const location = useLocation();
  return <Navigate replace to={`/tenant/overview${location.search}`} />;
}

function MeetingJoinHost() {
  const { inviteCode = "" } = useParams<{ inviteCode: string }>();
  return <ExternalAppHost appName="meeting" path={inviteCode} />;
}

function Authenticated() {
  const session = useSession();
  const sessionReady = useSessionReady();
  const location = useLocation();
  if (!sessionReady) return <PageLoader label="正在恢复会话" />;
  if (session) return <Outlet />;
  if (isShareableDocsLocation(location.pathname, location.search)) {
    return (
      <Suspense fallback={<PageLoader label="正在打开公开文档" />}>
        <ExternalAppHost appName="docs" path="" />
      </Suspense>
    );
  }
  const redirect = `${location.pathname}${location.search}`;
  return (
    <Navigate
      replace
      to={`/auth/login?redirect=${encodeURIComponent(redirect)}`}
    />
  );
}

function Anonymous() {
  const session = useSession();
  const sessionReady = useSessionReady();
  if (!sessionReady) return <PageLoader label="正在恢复会话" />;
  return session ? <Navigate replace to="/" /> : <Outlet />;
}

function RootApplication() {
  const location = useLocation();
  if (isDesktopRuntime()) {
    return <Navigate replace to={desktopHomeRoute(location.search)} />;
  }
  return <RemoteHome />;
}

function RemoteHome() {
  const navigate = useNavigate();
  const applications = useApplications();
  const runtime = useMemo(
    () =>
      sessionManager.runtime((to) => {
        void openPreferredApplicationPath(
          (path) => void navigate(path),
          applications.data ?? [],
          to,
        ).then((opened) => {
          if (!opened) void navigate(to);
        });
      }),
    [applications.data, navigate],
  );
  return (
    <RemoteBoundary>
      <Suspense fallback={<PageLoader label="正在加载工作台" />}>
        <HomeApp runtime={runtime} />
        <ApplicationWorkspaceCommit />
      </Suspense>
    </RemoteBoundary>
  );
}

function WorkspaceSuspense({
  children,
  fallback,
}: {
  children: ReactNode;
  fallback: ReactNode;
}) {
  return (
    <Suspense fallback={fallback}>
      {children}
      <ApplicationWorkspaceCommit />
    </Suspense>
  );
}

class RemoteBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  override state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("federated app crashed", error, info);
  }

  override render() {
    if (!this.state.error) return this.props.children;
    return (
      <ErrorState
        message="工作台加载失败"
        onRetry={() => {
          this.setState({ error: null });
          window.location.reload();
        }}
      />
    );
  }
}
